/**
 * Order entity — the user's orders (list shape + status vocabulary).
 *
 * Mirrors the backend ``OrderListSerializer`` and ``OrderStatus`` (the 11 FSM
 * statuses, spec §5). Full order detail / chat live in Task 5.2; here we model the
 * list item and the status presentation used across the storefront.
 */
import type { BadgeTone } from "@melusine/shared-ui";

/** The 11 order statuses (must match ``apps.orders.states.OrderStatus``). */
export type OrderStatus =
  | "created"
  | "awaiting_seller"
  | "accepted"
  | "awaiting_payment"
  | "payment_declared"
  | "payment_check"
  | "paid"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "refund"
  | "disputed";

/**
 * Purchase-receipt request lifecycle (ТЗ №2доп §19-20), value-identical to the backend
 * ``ReceiptRequestState``. ``none`` — not requested; ``requested`` — buyer asked and the seller
 * is preparing it; ``fulfilled`` — the seller delivered the receipt and archived the order.
 */
export type ReceiptRequestState = "none" | "requested" | "fulfilled";

/** Compact order as returned by ``GET /api/orders/``. */
export interface OrderListItem {
  readonly id: number;
  readonly number: string;
  readonly game: number;
  readonly seller: number | null;
  readonly status: OrderStatus;
  readonly subtotal: string;
  readonly discount_total: string;
  readonly total_rub: string;
  /** Server-computed: order is COMPLETED and not yet reviewed (ТЗ §10/§23). */
  readonly can_review: boolean;
  /** Server-computed: the self-cancel window is still open (ТЗ §11). */
  readonly can_self_cancel: boolean;
  /** Seconds left in the self-cancel window (0 once elapsed; ТЗ §11). */
  readonly self_cancel_seconds_left: number;
  /** Whether the buyer may request a purchase receipt now (server-computed; ТЗ №2доп §19-20). */
  readonly can_request_receipt: boolean;
  /** Purchase-receipt request state (ТЗ №2доп §19-20). */
  readonly receipt_request_state: ReceiptRequestState | string;
  /** Line snapshots — drive the thumbnail + «+N» badge (ТЗ §7). */
  readonly items: readonly OrderItemSnapshot[];
  readonly created_at: string;
}

/** Snapshotted order line (price/title captured at order time — spec §4). */
export interface OrderItemSnapshot {
  readonly id: number;
  readonly product: number | null;
  readonly product_variant: number | null;
  readonly title_snapshot: string;
  /** RUB, DRF Decimal string. */
  readonly unit_price_snapshot: string;
  readonly qty: number;
  /** RUB, DRF Decimal string. */
  readonly line_total: string;
  /** Frozen product image URL captured at order time (ТЗ §7); ``""`` when absent. */
  readonly image_url_snapshot: string;
}

/** One status-history entry (auto-written on every FSM transition). */
export interface OrderStatusHistoryEntry {
  readonly id: number;
  readonly from_status: string;
  readonly to_status: string;
  readonly changed_by: number | null;
  readonly comment: string;
  readonly created_at: string;
}

/** Full order detail as returned by ``GET /api/orders/<id>/`` (mirrors OrderDetailSerializer). */
export interface OrderDetail {
  readonly id: number;
  readonly number: string;
  readonly user: number;
  readonly game: number;
  readonly seller: number | null;
  readonly status: OrderStatus;
  readonly subtotal: string;
  readonly discount_total: string;
  readonly primogem_spent_rub: string;
  readonly star_glitter_spent_rub: string;
  readonly primogem_spent: number;
  readonly star_glitter_spent: number;
  readonly total_rub: string;
  readonly user_comment: string;
  /** Customer requested a payment receipt at checkout (ТЗ №2доп §16-18). */
  readonly needs_receipt: boolean;
  /** Whether the buyer may request a purchase receipt now (server-computed; ТЗ №2доп §19-20). */
  readonly can_request_receipt: boolean;
  /** Purchase-receipt request state (ТЗ №2доп §19-20). */
  readonly receipt_request_state: ReceiptRequestState | string;
  /** Server-computed: order is COMPLETED and not yet reviewed (ТЗ §10/§23). */
  readonly can_review: boolean;
  /**
   * Server-computed: whether the chat currently accepts new messages/attachments. True for
   * non-terminal orders and for COMPLETED while a purchase receipt is requested; false
   * otherwise (mirrors ``Order.is_chat_postable``). Drives the composer lock, not the status.
   */
  readonly chat_postable: boolean;
  /**
   * Role-aware FSM transitions the current requester may drive on this order (ТЗ §2.3).
   * For a Telegram customer this includes ``declare_payment`` while awaiting payment.
   */
  readonly available_transitions: readonly string[];
  /** Server-computed: the self-cancel window is still open (ТЗ §11). */
  readonly can_self_cancel: boolean;
  /** Seconds left in the self-cancel window (0 once elapsed; ТЗ §11). */
  readonly self_cancel_seconds_left: number;
  readonly items: readonly OrderItemSnapshot[];
  readonly status_history: readonly OrderStatusHistoryEntry[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** A persisted order chat message (mirrors ``ChatMessageSerializer``). */
export interface OrderChatMessage {
  readonly id: number;
  readonly order: number;
  readonly sender: number | null;
  readonly sender_role: string;
  readonly text: string;
  readonly attachment_url: string;
  readonly source: string;
  readonly is_read: boolean;
  readonly created_at: string;
}

/** Statuses in which an order is still "open" / awaiting fulfilment. */
const OPEN_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  "created",
  "awaiting_seller",
  "accepted",
  "awaiting_payment",
  "payment_declared",
  "payment_check",
  "paid",
  "in_progress",
]);

/** ``true`` while the order is in flight (chat/"call seller" stay relevant). */
export function isOrderOpen(status: OrderStatus): boolean {
  return OPEN_STATUSES.has(status);
}

/**
 * Statuses in which a customer self-cancel could ever apply (mirrors the backend
 * ``CUSTOMER_CANCELLABLE``). Used to decide whether the «отмена недоступна» hint is relevant
 * once the time window has elapsed (ТЗ §11) — a completed/cancelled order shows nothing.
 */
const SELF_CANCEL_STAGES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  "awaiting_seller",
  "accepted",
  "awaiting_payment",
]);

/** ``true`` when the order is at a stage where self-cancellation is conceptually relevant. */
export function isSelfCancelStage(status: OrderStatus): boolean {
  return SELF_CANCEL_STAGES.has(status);
}

/**
 * Terminal statuses — the order is closed and the chat is read-only.
 * Mirrors the server, which rejects writes to these states (completed/cancelled/refund).
 */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set<string>([
  "completed",
  "cancelled",
  "refund",
]);

/** ``true`` when the order is closed (completed/cancelled/refund) — chat is locked. */
export function isOrderTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** The visual summary of an order's lines for «Мои заказы» (ТЗ §7). */
export interface OrderThumbnail {
  /** Image URL of the headline (most expensive) line, or ``""`` when none has art. */
  readonly imageUrl: string;
  /** Title of the headline line (thumbnail fallback / alt text). */
  readonly title: string;
  /** Extra distinct lines beyond the headline one — drives the «+N» badge (0 = none). */
  readonly extraCount: number;
}

/**
 * Pick an order's headline line and how many other distinct lines it has (ТЗ §7).
 *
 * The headline is the most expensive line (by ``line_total``); multi-line orders show its
 * art plus a «+N» badge for the remaining distinct products. Amounts are compared only for
 * ordering — never re-derived for economic decisions.
 */
export function orderThumbnail(
  items: readonly OrderItemSnapshot[],
): OrderThumbnail {
  if (items.length === 0) {
    return { imageUrl: "", title: "", extraCount: 0 };
  }
  let headline = items[0] as OrderItemSnapshot;
  for (const item of items) {
    if (Number(item.line_total) > Number(headline.line_total)) {
      headline = item;
    }
  }
  return {
    imageUrl: headline.image_url_snapshot,
    title: headline.title_snapshot,
    extraCount: Math.max(0, items.length - 1),
  };
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i;

/** ``true`` when an attachment URL points at a renderable image. */
export function isImageAttachment(url: string): boolean {
  return IMAGE_EXT.test(url);
}

interface StatusPresentation {
  readonly label: string;
  readonly tone: BadgeTone;
}

/** Russian label + badge tone for each order status. */
export const ORDER_STATUS_PRESENTATION: Readonly<
  Record<OrderStatus, StatusPresentation>
> = {
  created: { label: "Создан", tone: "neutral" },
  awaiting_seller: { label: "Ожидает продавца", tone: "warning" },
  accepted: { label: "Принят", tone: "amethyst" },
  awaiting_payment: { label: "Ожидает оплаты", tone: "warning" },
  payment_declared: { label: "Покупатель сообщил об оплате", tone: "warning" },
  payment_check: { label: "Проверка оплаты", tone: "warning" },
  paid: { label: "Оплачен", tone: "gold" },
  in_progress: { label: "В работе", tone: "amethyst" },
  completed: { label: "Выполнен", tone: "success" },
  cancelled: { label: "Отменён", tone: "neutral" },
  refund: { label: "Возврат", tone: "danger" },
  disputed: { label: "Спор", tone: "danger" },
};
